import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiPackage, FiLayers, FiPlusCircle, FiRepeat, FiSettings, FiBox, FiX } from "react-icons/fi";
import { createPortal } from "react-dom";
import "../styles/Refrigerator.css";

import {
  getUserFridgesApi,
  getFridgeItemsApi,
  getFreezerItemsApi,
  updateFridgeItemApi,
  updateFreezerItemApi,
  deleteFridgeItemApi,
  deleteFreezerItemApi,
  createFridgeItemApi,
  createFreezerItemApi,
  getPantryItemsApi,
  createPantryItemApi,
  updatePantryItemApi,
  deletePantryItemApi,
} from "../api/refrigerator";
import { getUserProfile } from "../api/auth";
import { clearAuthTokens, ensureAccessToken } from "../api/apiClient";
import { getUserIdFromToken, isLoggedIn } from "../utils/jwt";
import { useToast } from "../context/ToastContext";
import { ITEM_SORT_OPTIONS, sortItems, normalizeSortKey } from "../utils/itemSort";
import { formatDdayLabel, formatRegisteredAt, getDaysUntilExpiration, isDdayUrgent } from "../utils/ddayLabel";
import { CatalogIngredientGlyph, INGREDIENT_IMAGE_OVERRIDES } from "../constants/ingredientCatalogVisuals";
import { getIngredientImageMapApi } from "../api/ingredientImages";
import { getIngredientCatalogIconMapApi } from "../api/ingredientCatalog";
import { getGuestIngredientImageMapForCustomCatalogOnly } from "../utils/guestIngredientImages";
import { playFlipSfx } from "../utils/sfx";
import { syncExpiryLocalNotifications } from "../utils/expiryLocalNotifications";
import Loading from "./Loading";

const UNIT_OPTIONS = ["개", "팩", "병", "봉지", "캔", "g", "kg", "ml", "L"];
const LS_FRIDGE_SORT = "yatang_fridge_sort";

const asArray = (value) => (Array.isArray(value) ? value : []);

const Refrigerator = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [userNickname, setUserNickname] = useState("");
  const [mainFridge, setMainFridge] = useState(null);
  const [fridges, setFridges] = useState([]);
  const [fridgeItems, setFridgeItems] = useState([]);
  const [freezerItems, setFreezerItems] = useState([]);
  const [pantryItems, setPantryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isFlipped, setIsFlipped] = useState(false);
  const [isFlipSpinning, setIsFlipSpinning] = useState(false);

  const [detailItem, setDetailItem] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [editValues, setEditValues] = useState(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [sortKey, setSortKey] = useState(() => normalizeSortKey(localStorage.getItem(LS_FRIDGE_SORT)));

  /** 재료명(소문자) → 사용자 등록 이미지 URL */
  const [ingredientImageMap, setIngredientImageMap] = useState({});
  /** 재료명(소문자) → 시스템 카탈로그 아이콘 파일명(영문) */
  const [systemIconFileByNameLower, setSystemIconFileByNameLower] = useState({});

  useEffect(() => {
    localStorage.setItem(LS_FRIDGE_SORT, sortKey);
  }, [sortKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await getIngredientCatalogIconMapApi();
        if (!cancelled) setSystemIconFileByNameLower(m && typeof m === "object" ? m : {});
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const syncGuest = () => {
      if (!isLoggedIn()) {
        setIngredientImageMap(getGuestIngredientImageMapForCustomCatalogOnly());
      }
    };
    syncGuest();
    window.addEventListener("yatang-guest-images-changed", syncGuest);
    return () => window.removeEventListener("yatang-guest-images-changed", syncGuest);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isLoggedIn()) {
        setIngredientImageMap(getGuestIngredientImageMapForCustomCatalogOnly());
        return;
      }
      const uid = getUserIdFromToken();
      if (!uid) return;
      try {
        const m = await getIngredientImageMapApi();
        if (!cancelled) setIngredientImageMap(m || {});
      } catch (e) {
        console.error(e);
      }
    };
    load();
    const onServer = () => {
      if (isLoggedIn()) load();
    };
    window.addEventListener("yatang-ingredient-images-changed", onServer);
    return () => {
      cancelled = true;
      window.removeEventListener("yatang-ingredient-images-changed", onServer);
    };
  }, [loading, mainFridge?.id]);

  const sortedFridgeItems = useMemo(() => sortItems(fridgeItems, sortKey), [fridgeItems, sortKey]);
  const sortedFreezerItems = useMemo(() => sortItems(freezerItems, sortKey), [freezerItems, sortKey]);
  const sortedPantryItems = useMemo(() => sortItems(pantryItems, sortKey), [pantryItems, sortKey]);
  const sortedFridges = useMemo(
    () => [...asArray(fridges)].sort((a, b) => Number(b.isMain === true) - Number(a.isMain === true)),
    [fridges],
  );

  // ───────── 유통기한 로컬 알림 (D-1·당일 오전 9시) + 앱 내 토스트(하루 1회) ─────────
  useEffect(() => {
    if (loading) return;

    syncExpiryLocalNotifications({
      fridgeItems,
      freezerItems,
      pantryItems,
      nickname: userNickname,
    }).catch((e) => console.warn("expiry local notifications:", e));

    const all = [...(fridgeItems || []), ...(freezerItems || []), ...(pantryItems || [])];
    if (all.length === 0) return;

    const dayKey = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const LS_KEY = "yatang_expiry_notify_day";
    if (localStorage.getItem(LS_KEY) === dayKey) return;

    const expiring = all
      .map((it) => {
        const d = getDaysUntilExpiration(it);
        return { item: it, days: d };
      })
      .filter((x) => x.item?.expirationDate && x.days != null && x.days <= 1); // D-1부터 + 지난 것까지

    if (expiring.length === 0) return;
    expiring.sort((a, b) => (a.days ?? 999) - (b.days ?? 999));

    const top = expiring.slice(0, 3).map((x) => x.item.name).filter(Boolean);
    const msg =
      top.length === 0
        ? "유통기한 임박 재료가 있습니다."
        : `유통기한 임박: ${top.join(", ")}${expiring.length > 3 ? ` 외 ${expiring.length - 3}개` : ""}`;

    toast(msg);
    localStorage.setItem(LS_KEY, dayKey);
  }, [loading, fridgeItems, freezerItems, pantryItems, userNickname, toast]);

  const refreshItems = async (targetFridgeId) => {
    const [fridgeData, freezerData] = await Promise.all([
      getFridgeItemsApi(targetFridgeId),
      getFreezerItemsApi(targetFridgeId),
    ]);
    setFridgeItems(asArray(fridgeData));
    setFreezerItems(asArray(freezerData));
  };

  const refreshPantry = useCallback(async () => {
    const data = await getPantryItemsApi();
    setPantryItems(asArray(data));
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        let loggedIn = isLoggedIn();
        if (loggedIn) {
          const ok = await ensureAccessToken();
          if (!ok) loggedIn = false;
        }

        if (loggedIn) {
          try {
            const profile = await getUserProfile();
            setUserNickname(profile.nickname || profile.username || "");
          } catch (e) {
            const st = e.response?.status;
            if (st === 401 || st === 403) throw e;
            console.error(e);
            setUserNickname("");
          }
        } else {
          setUserNickname("게스트");
        }

        const fridgeList = asArray(await getUserFridgesApi());
        setFridges(fridgeList);

        if (fridgeList.length > 0) {
          const main = fridgeList.find((f) => f.isMain) || fridgeList[0];
          setMainFridge(main);
          await refreshItems(main.id);
        }
        await refreshPantry();
      } catch (error) {
        console.error("데이터 로딩 에러:", error);
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          clearAuthTokens();
          toast("로그인이 만료되었습니다. 다시 로그인해주세요.");
          navigate("/signin", { replace: true });
          return;
        }
        toast("데이터를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate, refreshPantry, toast]);

  const handleFridgeChange = async (e) => {
    const selectedId = e.target.value;
    const selected = fridges.find((f) => String(f.id) === String(selectedId));
    if (selected) {
      setLoading(true);
      setMainFridge(selected);
      await refreshItems(selected.id);
      setLoading(false);
    }
  };

  const handleFridgeSelectChange = async (e) => {
    const v = e.target.value;
    if (v === "__manage__") {
      navigate("/settings", { state: { settingsTab: "fridge" } });
      return;
    }
    await handleFridgeChange(e);
  };

  const goIngredientAdd = () => {
    navigate("/ingredient", { state: { selectedFridgeId: mainFridge?.id ?? null, fromRefrigerator: true } });
  };

  const totalItems = useMemo(
    () => fridgeItems.length + freezerItems.length + pantryItems.length,
    [fridgeItems.length, freezerItems.length, pantryItems.length],
  );

  const openDetail = (item, type) => {
    setDetailItem(item);
    setDetailType(type);
    setEditValues({
      name: item.name || "",
      quantity: item.quantity ?? 1,
      unit: item.unit || "개",
      expirationDate: item.expirationDate || "",
      manufactureDate: item.manufactureDate || "",
      memo: item.memo || "",
    });
  };

  const closeDetail = () => {
    setDetailItem(null);
    setDetailType(null);
    setEditValues(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditValues((prev) => ({
      ...prev,
      [name]: name === "quantity" ? Number(value) || 0 : value,
    }));
  };

  const buildPayloadFromEdit = () => {
    if (!editValues) return null;
    const q = Number(editValues.quantity);
    return {
      name: (editValues.name || "").trim(),
      quantity: Number.isNaN(q) || q < 1 ? 1 : q,
      unit: (editValues.unit || "개").trim(),
      expirationDate: editValues.expirationDate || null,
      manufactureDate: editValues.manufactureDate || null,
      memo: editValues.memo || null,
    };
  };

  const handleUpdate = async () => {
    if (!detailItem || !detailType || !editValues) return;
    if (detailType !== "pantry" && !mainFridge) return;
    const body = buildPayloadFromEdit();
    if (!body?.name) {
      toast("이름을 입력해주세요.");
      return;
    }
    try {
      if (detailType === "fridge") {
        await updateFridgeItemApi(mainFridge.id, detailItem.id, body);
        await refreshItems(mainFridge.id);
      } else if (detailType === "freezer") {
        await updateFreezerItemApi(mainFridge.id, detailItem.id, body);
        await refreshItems(mainFridge.id);
      } else {
        await updatePantryItemApi(detailItem.id, body);
        await refreshPantry();
      }
      toast("저장했습니다.");
      closeDetail();
    } catch (error) {
      console.error("아이템 수정 에러:", error);
      toast(error.message || "아이템 수정에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!detailItem || !detailType) return;
    if (detailType !== "pantry" && !mainFridge) return;
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      if (detailType === "fridge") {
        await deleteFridgeItemApi(mainFridge.id, detailItem.id);
        await refreshItems(mainFridge.id);
      } else if (detailType === "freezer") {
        await deleteFreezerItemApi(mainFridge.id, detailItem.id);
        await refreshItems(mainFridge.id);
      } else {
        await deletePantryItemApi(detailItem.id);
        await refreshPantry();
      }
      toast("삭제했습니다.");
      closeDetail();
    } catch (error) {
      console.error("아이템 삭제 에러:", error);
      toast(error.message || "아이템 삭제에 실패했습니다.");
    }
  };

  const runMove = async (fn) => {
    const body = buildPayloadFromEdit();
    if (!body?.name || body.quantity < 1) {
      toast("이름·수량을 확인해주세요.");
      return;
    }
    setMoveBusy(true);
    try {
      await fn(body);
      await refreshItems(mainFridge.id);
      await refreshPantry();
      toast("이동했습니다.");
      closeDetail();
    } catch (error) {
      console.error(error);
      toast(error.message || "이동에 실패했습니다.");
    } finally {
      setMoveBusy(false);
    }
  };

  const handleMoveFridgeToFreezer = () => {
    if (!detailItem || !mainFridge || detailType !== "fridge") return;
    runMove(async (body) => {
      await deleteFridgeItemApi(mainFridge.id, detailItem.id);
      await createFreezerItemApi(mainFridge.id, body);
    });
  };

  const handleMoveFreezerToFridge = () => {
    if (!detailItem || !mainFridge || detailType !== "freezer") return;
    runMove(async (body) => {
      await deleteFreezerItemApi(mainFridge.id, detailItem.id);
      await createFridgeItemApi(mainFridge.id, body);
    });
  };

  const handleMoveToPantry = () => {
    if (!detailItem || !mainFridge) return;
    if (detailType !== "fridge" && detailType !== "freezer") return;
    runMove(async (body) => {
      if (detailType === "fridge") {
        await deleteFridgeItemApi(mainFridge.id, detailItem.id);
      } else {
        await deleteFreezerItemApi(mainFridge.id, detailItem.id);
      }
      await createPantryItemApi(body);
    });
  };

  const handleMovePantryToFridge = () => {
    if (!detailItem || !mainFridge || detailType !== "pantry") return;
    runMove(async (body) => {
      await deletePantryItemApi(detailItem.id);
      await createFridgeItemApi(mainFridge.id, body);
    });
  };

  const handleMovePantryToFreezer = () => {
    if (!detailItem || !mainFridge || detailType !== "pantry") return;
    runMove(async (body) => {
      await deletePantryItemApi(detailItem.id);
      await createFreezerItemApi(mainFridge.id, body);
    });
  };

  const renderGridItems = (items, type) =>
    items.map((item) => {
      const d = getDaysUntilExpiration(item);
      const dlabel = item.expirationDate ? formatDdayLabel(d) : null;
      const urgent = item.expirationDate ? isDdayUrgent(d) : false;
      const trimmedName = (item.name || "").trim();
      const lower = trimmedName.toLowerCase();
      const imgUrl = ingredientImageMap[lower];
      const iconFile = systemIconFileByNameLower[lower];
      const hasStaticCatalogImage = Boolean(
        (trimmedName && INGREDIENT_IMAGE_OVERRIDES[trimmedName]) || iconFile,
      );
      const showCatalogGlyph = Boolean(imgUrl || hasStaticCatalogImage);
      const isUserPhoto = Boolean(imgUrl);
      const iconModifier =
        showCatalogGlyph && (isUserPhoto ? " fridge-item-icon--photo" : " fridge-item-icon--catalog-icon");
      return (
        <div
          className="fridge-item-slot"
          key={`${type}-${item.id}`}
          onClick={() => openDetail(item, type)}
        >
          <div className={`fridge-item-icon ${type}${iconModifier || ""}`}>
            {showCatalogGlyph ? (
              <CatalogIngredientGlyph
                name={item.name}
                userImageUrl={imgUrl || undefined}
                iconImageFile={iconFile || undefined}
                className="fridge-item-photo-glyph"
              />
            ) : type === "fridge" ? (
              <FiPackage />
            ) : type === "freezer" ? (
              <FiLayers />
            ) : (
              <FiBox />
            )}
          </div>
          <div className="fridge-item-text-col">
            <div className="fridge-item-name">{item.name}</div>
            {dlabel && (
              <div className={`fridge-item-dday ${urgent ? "fridge-item-dday--urgent" : ""}`}>{dlabel}</div>
            )}
          </div>
        </div>
      );
    });

  if (loading) {
    return <Loading message="냉장고를 불러오는 중…" />;
  }

  const modalTitle =
    detailType === "fridge" ? "냉장실 재료 상세" : detailType === "freezer" ? "냉동실 재료 상세" : "상온보관 재료 상세";

  return (
    <div className="fridge-page-wrapper">
      <header className="fridge-header">
        <div className="fridge-header-top-row">
          <div className="fridge-header-content">
            {fridges.length > 1 ? (
              <div className="fridge-picker">
                <span className="fridge-picker-label">냉장고 선택</span>
                <div className="fridge-header-title-row">
                  <div className="fridge-select-control">
                    <span className="fridge-select-face" aria-hidden="true">
                      <span className="fridge-select-chevron" />
                      <span className="fridge-select-name">
                        {(mainFridge?.name || "").trim() || "냉장고"}
                      </span>
                    </span>
                    <select
                      className="fridge-select-dropdown"
                      value={mainFridge?.id || ""}
                      onChange={handleFridgeSelectChange}
                      aria-label="냉장고 선택"
                    >
                      {sortedFridges.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                      {isLoggedIn() && (
                        <>
                          <option disabled value="__sep__">
                            ───────────
                          </option>
                          <option value="__manage__">냉장고 관리하기</option>
                        </>
                      )}
                    </select>
                  </div>
                  {mainFridge?.isMain === true && (
                    <span className="badge-main" title="메인 냉장고">
                      main
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <h1 className="fridge-title">
                <span>{mainFridge?.name || `${userNickname}의 냉장고`}</span>
                {mainFridge?.isMain === true && (
                  <span className="badge-main" title="메인 냉장고">
                    main
                  </span>
                )}
                {isLoggedIn() && (
                  <button
                    type="button"
                    className="fridge-inline-manage"
                    onClick={() => navigate("/settings", { state: { settingsTab: "fridge" } })}
                  >
                    냉장고 관리하기
                  </button>
                )}
              </h1>
            )}
          </div>
          <div className="fridge-header-actions" style={{ display: "flex", gap: "8px" }}>
            <button className="fridge-logout-button" onClick={() => navigate("/settings")} title="설정">
              <FiSettings size={18} />
            </button>
            {!isLoggedIn() && (
              <button className="fridge-logout-button" onClick={() => navigate("/signin")}>
                로그인
              </button>
            )}
          </div>
        </div>
        <div className="fridge-subtitle-sort-row">
          <p className="fridge-subtitle">{mainFridge?.description || "등록된 재료를 한눈에 관리해보세요."}</p>
          <div className="fridge-sort-toolbar fridge-sort-toolbar--inline fridge-sort-toolbar--compact">
            <label className="fridge-sort-label" htmlFor="fridge-sort-select">
              정렬
            </label>
            <select
              id="fridge-sort-select"
              className="fridge-sort-select fridge-sort-select--compact"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
            >
              {ITEM_SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <div className="fridge-flip-container">
        <div
          className={`fridge-panel ${isFlipped ? "fridge-panel--freezer" : "fridge-panel--fridge"}${
            isFlipSpinning ? " fridge-panel--spinning" : ""
          }`}
        >
          <div className="fridge-face-header">
            {isFlipped ? (
              <>
                <div className="fridge-face-title">
                  <FiLayers size={24} color="#06b6d4" /> 냉동실
                </div>
                <button
                  type="button"
                  className="fridge-switch-button"
                  disabled={isFlipSpinning}
                  onClick={() => {
                    if (isFlipSpinning) return;
                    playFlipSfx();
                    setIsFlipSpinning(true);
                    window.setTimeout(() => setIsFlipped(false), 275);
                    window.setTimeout(() => setIsFlipSpinning(false), 560);
                  }}
                >
                  <FiRepeat /> 냉장실 보기
                </button>
              </>
            ) : (
              <>
                <div className="fridge-face-title">
                  <FiPackage size={24} color="#4c6ef5" /> 냉장실
                </div>
                <button
                  type="button"
                  className="fridge-switch-button"
                  disabled={isFlipSpinning}
                  onClick={() => {
                    if (isFlipSpinning) return;
                    playFlipSfx();
                    setIsFlipSpinning(true);
                    window.setTimeout(() => setIsFlipped(true), 275);
                    window.setTimeout(() => setIsFlipSpinning(false), 560);
                  }}
                >
                  <FiRepeat /> 냉동실 보기
                </button>
              </>
            )}
          </div>
          <div className="fridge-grid-content">
            {isFlipped ? (
              freezerItems.length === 0 ? (
                <div className="fridge-empty-text">냉동실이 비어있습니다.</div>
              ) : (
                renderGridItems(sortedFreezerItems, "freezer")
              )
            ) : fridgeItems.length === 0 ? (
              <div className="fridge-empty-text">냉장실이 비어있습니다.</div>
            ) : (
              renderGridItems(sortedFridgeItems, "fridge")
            )}
          </div>
        </div>
      </div>

      <section className="fridge-pantry-section">
        <div className="fridge-pantry-header">
          <div className="fridge-pantry-title">
            <FiBox size={22} color="#b45309" /> 상온보관
          </div>
          <span className="fridge-pantry-hint">냉장·냉동과 별도로 두는 재료 (사용자당 한 공간)</span>
        </div>
        <div className="fridge-pantry-grid">
          {pantryItems.length === 0 ? (
            <div className="fridge-empty-text fridge-pantry-empty">상온 보관 중인 재료가 없습니다.</div>
          ) : (
            renderGridItems(sortedPantryItems, "pantry")
          )}
        </div>
      </section>

      <div className="fridge-summary-row fridge-summary-row-four">
        <div className="fridge-summary-card">
          <div className="fridge-summary-icon variant-fridge">
            <FiPackage size={28} />
          </div>
          <div className="fridge-summary-text">
            <span>냉장실</span>
            <strong>{fridgeItems.length}개</strong>
          </div>
        </div>
        <div className="fridge-summary-card">
          <div className="fridge-summary-icon variant-freezer">
            <FiLayers size={28} />
          </div>
          <div className="fridge-summary-text">
            <span>냉동실</span>
            <strong>{freezerItems.length}개</strong>
          </div>
        </div>
        <div className="fridge-summary-card">
          <div className="fridge-summary-icon variant-pantry">
            <FiBox size={28} />
          </div>
          <div className="fridge-summary-text">
            <span>상온보관</span>
            <strong>{pantryItems.length}개</strong>
          </div>
        </div>
        <div className="fridge-summary-card">
          <div className="fridge-summary-icon variant-all">
            <FiPlusCircle size={28} />
          </div>
          <div className="fridge-summary-text">
            <span>전체 재료</span>
            <strong>{totalItems}개</strong>
          </div>
        </div>
      </div>

      {!detailItem && typeof document !== "undefined"
        ? createPortal(
            <button type="button" className="fridge-add-fab" onClick={goIngredientAdd}>
              재료추가
            </button>,
            document.body,
          )
        : null}

      {detailItem && editValues && typeof document !== "undefined"
        ? createPortal(
            <div className="fridge-modal-backdrop" onClick={closeDetail}>
              <div className="fridge-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fridge-modal-header">
              <h2 className="fridge-modal-title">{modalTitle}</h2>
              <button type="button" className="fridge-modal-close-x" onClick={closeDetail} aria-label="닫기">
                <FiX size={20} />
              </button>
            </div>
            <div className="fridge-modal-field">
              <label className="fridge-modal-label">등록일</label>
              <input
                className="fridge-modal-input fridge-modal-input-readonly"
                readOnly
                value={formatRegisteredAt(detailItem.createdAt)}
              />
            </div>
            <div className="fridge-modal-field">
              <label className="fridge-modal-label">이름</label>
              <input className="fridge-modal-input" name="name" value={editValues.name} onChange={handleEditChange} />
            </div>
            <div className="fridge-modal-row">
              <div className="fridge-modal-field">
                <label className="fridge-modal-label">수량</label>
                <input
                  className="fridge-modal-input"
                  type="number"
                  name="quantity"
                  min="1"
                  value={editValues.quantity}
                  onChange={handleEditChange}
                />
              </div>
              <div className="fridge-modal-field">
                <label className="fridge-modal-label">단위</label>
                <select className="fridge-modal-select" name="unit" value={editValues.unit} onChange={handleEditChange}>
                  {UNIT_OPTIONS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="fridge-modal-row">
              <div className="fridge-modal-field">
                <label className="fridge-modal-label">소비기한</label>
                <input
                  className="fridge-modal-input"
                  type="date"
                  name="expirationDate"
                  value={editValues.expirationDate || ""}
                  onChange={handleEditChange}
                />
              </div>
              <div className="fridge-modal-field">
                <label className="fridge-modal-label">제조일자</label>
                <input
                  className="fridge-modal-input"
                  type="date"
                  name="manufactureDate"
                  value={editValues.manufactureDate || ""}
                  onChange={handleEditChange}
                />
              </div>
            </div>
            <div className="fridge-modal-field">
              <label className="fridge-modal-label">메모</label>
              <input
                className="fridge-modal-input"
                name="memo"
                placeholder="보관 위치나 메모를 입력하세요."
                value={editValues.memo}
                onChange={handleEditChange}
              />
            </div>

            {mainFridge && (detailType === "fridge" || detailType === "freezer") && (
              <div className="fridge-modal-move-row">
                {detailType === "fridge" ? (
                  <>
                    <button
                      type="button"
                      className="fridge-modal-move-btn"
                      disabled={moveBusy}
                      onClick={handleMoveFridgeToFreezer}
                    >
                      냉동실로 이동
                    </button>
                    <button
                      type="button"
                      className="fridge-modal-move-btn"
                      disabled={moveBusy}
                      onClick={handleMoveToPantry}
                    >
                      상온보관으로 이동
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="fridge-modal-move-btn"
                      disabled={moveBusy}
                      onClick={handleMoveFreezerToFridge}
                    >
                      냉장실로 이동
                    </button>
                    <button
                      type="button"
                      className="fridge-modal-move-btn"
                      disabled={moveBusy}
                      onClick={handleMoveToPantry}
                    >
                      상온보관으로 이동
                    </button>
                  </>
                )}
              </div>
            )}

            {mainFridge && detailType === "pantry" && (
              <div className="fridge-modal-move-row">
                <button
                  type="button"
                  className="fridge-modal-move-btn"
                  disabled={moveBusy}
                  onClick={handleMovePantryToFridge}
                >
                  냉장실로 이동
                </button>
                <button
                  type="button"
                  className="fridge-modal-move-btn"
                  disabled={moveBusy}
                  onClick={handleMovePantryToFreezer}
                >
                  냉동실로 이동
                </button>
              </div>
            )}

            <div className="fridge-modal-actions">
              <button className="fridge-modal-delete-btn" type="button" onClick={handleDelete}>
                삭제
              </button>
              <button className="fridge-modal-primary-btn" type="button" onClick={handleUpdate}>
                수정 사항 저장
              </button>
            </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default Refrigerator;
